// ─────────────────────────────────────────────────────────────
// SongCatalogView — searchable list of all songs in the API
// ─────────────────────────────────────────────────────────────

import {createElement} from "@opendaw/lib-jsx"
import {Lifecycle} from "@opendaw/lib-std"
import {StudioService} from "@/service/StudioService.ts"
import {STApi} from "../STApi"
import {STSong} from "../STTypes"

type Construct = {lifecycle: Lifecycle; service: StudioService}

const KEY_NAMES: Record<number, string> = {
    0:"C", 1:"C#", 2:"D", 3:"D#", 4:"E", 5:"F",
    6:"F#", 7:"G", 8:"G#", 9:"A", 10:"A#", 11:"B"
}

export const SongCatalogView = ({}: Construct) => {
    let allSongs: STSong[] = []
    let searchQuery = ""

    const listEl: HTMLElement = <div/>
    const searchInput: HTMLInputElement = (
        <input className="st-input" type="text" placeholder="Search songs…" style={{width: "100%", boxSizing: "border-box"}}/>
    )

    const statusBadgeStyle = (status: string): Partial<CSSStyleDeclaration> => {
        if (status === "published") return {color: "#80c080", fontSize: "0.6rem"}
        if (status === "draft")     return {color: "#c0b060", fontSize: "0.6rem"}
        return {color: "var(--color-dark)", fontSize: "0.6rem"}
    }

    const renderList = () => {
        listEl.innerHTML = ""
        const filtered = allSongs.filter(s => {
            const q = searchQuery.toLowerCase()
            return s.title.toLowerCase().includes(q) ||
                   (s.artist ?? "").toLowerCase().includes(q) ||
                   (s.type?.name ?? "").toLowerCase().includes(q)
        })

        if (filtered.length === 0) {
            listEl.appendChild(
                <div className="st-empty">
                    {allSongs.length === 0 ? "No songs in library yet." : "No songs match your search."}
                </div>
            )
            return
        }

        for (const song of filtered) {
            const keyName = song.key != null ? KEY_NAMES[song.key % 12] ?? "?" : "?"
            const bpmStr  = song.bpm   ? `${song.bpm} BPM` : ""
            const tsStr   = song.timeSignatureTop ? `${song.timeSignatureTop}/${song.timeSignatureBottom ?? 4}` : ""
            const typeColor = song.type?.color ?? "#7c5cbf"

            const item: HTMLElement = (
                <div className="st-catalog-item">
                    <div className="st-catalog-title">{song.title}{song.artist ? ` — ${song.artist}` : ""}</div>
                    <div className="st-catalog-meta">
                        <span style={statusBadgeStyle(song.status)}>{song.status}</span>
                        {song.type && (
                            <span className="st-badge" style={{
                                backgroundColor: `${typeColor}33`,
                                color: typeColor,
                                border: `1px solid ${typeColor}55`,
                                fontSize: "0.58rem",
                                padding: "0.1em 0.4em",
                            }}>{song.type.name}</span>
                        )}
                        {keyName && <span style={{fontSize:"0.6rem",color:"var(--color-dark)"}}>Key: {keyName}</span>}
                        {bpmStr  && <span style={{fontSize:"0.6rem",color:"var(--color-dark)"}}>{bpmStr}</span>}
                        {tsStr   && <span style={{fontSize:"0.6rem",color:"var(--color-dark)"}}>{tsStr}</span>}
                        {song.plays_count != null && (
                            <span style={{fontSize:"0.6rem",color:"var(--color-dark)"}}>▶ {song.plays_count}</span>
                        )}
                    </div>
                    <div className="st-catalog-actions">
                        <div className="st-btn" data-songid={song.id} data-action="open">Open in DAW</div>
                    </div>
                </div>
            )
            listEl.appendChild(item)
        }

        // Wire Open in DAW buttons
        listEl.querySelectorAll<HTMLElement>("[data-action='open']").forEach(btn => {
            btn.addEventListener("click", () => {
                const id = btn.getAttribute("data-songid") ?? ""
                const song = allSongs.find(s => s.id === id)
                if (!song) return
                // TODO: look up local OPFS project by st_db_id and open it
                // For now, show a reminder so the user knows which project this maps to
                alert(`Open project for: "${song.title}"\n\nFind the matching project in the Dashboard and open it from there.`)
            })
        })
    }

    const loadSongs = async () => {
        listEl.innerHTML = ""
        listEl.appendChild(<div className="st-empty">Loading…</div>)
        try {
            allSongs = await STApi.getSongs()
            renderList()
        } catch (err) {
            listEl.innerHTML = ""
            listEl.appendChild(
                <div className="st-empty" style={{color:"#ff8080"}}>
                    Failed to load songs.<br/>Check your connection.
                </div>
            )
        }
    }

    searchInput.addEventListener("input", () => {
        searchQuery = searchInput.value
        renderList()
    })

    // Load on mount
    loadSongs()

    return (
        <div style={{display:"flex", flexDirection:"column", height:"100%"}}>
            <div className="st-search">{searchInput}</div>
            {listEl}
        </div>
    )
}
